import { Component, computed, inject, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { Company } from 'src/app/user/model/user';
import { CompanyService } from '../../services/company.service';
import { TableModule } from 'primeng/table';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService } from 'primeng/api';
import { NotificationService } from 'src/app/core/services/notification.service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { SelectModule } from 'primeng/select';
import { IftaLabelModule } from 'primeng/iftalabel';
import { DownloadCompanyListComponent } from '../download-company-list/download-company-list.component';
import { PackService } from '../../services/pack.service';

@Component({
  selector: 'app-manage-companies',
  imports: [
    ButtonModule,
    RouterLink,
    CommonModule,
    TableModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    FormsModule,
    TagModule,
    TooltipModule,
    ConfirmDialogModule,
    ToastModule,
    SelectModule,
    IftaLabelModule,
    DownloadCompanyListComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './manage-companies.component.html',
})
export class ManageCompaniesComponent implements OnInit {
  companies: Company[] = [];
  deletedCompanies: Company[] = [];
  showDeleted = false;
  companyService = inject(CompanyService);
  packService = inject(PackService);
  router = inject(Router);
  confirmationService = inject(ConfirmationService);
  notificationService = inject(NotificationService);
  globalFilter: string = '';
  packFilter: string | null = null;
  statusFilter: string | null = null;

  packOptions = computed(() => [
    { label: 'All Plans', value: null as string | null },
    ...(this.packService.packs() ?? []).map((pack) => ({
      label: pack.name,
      value: pack.name as string | null,
    })),
  ]);

  statusOptions = [
    { label: 'All Status', value: null },
    { label: 'Premium', value: 'premium' },
    { label: 'Discovery', value: 'discovery' },
    { label: 'En test', value: 'enTest' },
    { label: 'Contacté', value: 'contacted' },
    { label: 'Limite', value: 'limit' },
  ];

  get filteredCompanies(): Company[] {
    let result = this.showDeleted ? this.deletedCompanies : this.companies;

    if (this.packFilter) {
      result = result.filter((company) => company.pack?.name === this.packFilter);
    }

    if (this.statusFilter) {
      result = result.filter((company) => {
        const status = this.getStatusKey(company);
        return status === this.statusFilter;
      });
    }

    return result;
  }

  getStatusKey(company: Company): string {
    if (company.pack.code !== 'FREE') {
      return 'premium';
    }
    const used = company._count?.trips || 0;
    const limit = company.pack.tripLimit || 1;

    if (company.hasContactedSales) {
      return 'contacted';
    }
    if (used >= limit) {
      return 'limit';
    }
    if (used > 0) {
      return 'enTest';
    }
    return 'discovery';
  }

  ngOnInit() {
    this.loadCompanies();
  }

  resetFilters(): void {
    this.globalFilter = '';
    this.packFilter = null;
    this.statusFilter = null;
  }

  loadCompanies() {
    this.companyService.getCompanyList().subscribe({
      next: (companies) => {
        this.companies = companies;
      },
      error: (error) => {
        console.error('Error loading companies:', error);
      },
    });
  }

  loadDeletedCompanies() {
    this.companyService.getDeletedCompanies().subscribe({
      next: (companies) => {
        this.deletedCompanies = companies;
      },
      error: (error) => {
        console.error('Error loading deleted companies:', error);
      },
    });
  }

  toggleDeleted() {
    this.showDeleted = !this.showDeleted;
    this.resetFilters();
    if (this.showDeleted) {
      this.loadDeletedCompanies();
    } else {
      // Reload so companies reactivated while in the deleted view show up here.
      this.loadCompanies();
    }
  }

  formatDate(inputDate: Date): string {
    if (!inputDate) {
      return '--';
    }
    const date = new Date(inputDate);
    const formatted = date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Europe/Paris',
    });
    return formatted.replace(',', '');
  }

  /**
   * Récupère le statut compact avec compteur pour Discovery
   */
  getCompactStatus(company: Company): {
    label: string;
    progress?: string;
    severity: 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast';
    icon: string;
  } {
    if (company.pack.code !== 'FREE') {
      return { label: 'Premium', severity: 'success', icon: 'pi pi-check' };
    }

    const used = company._count?.trips || 0;
    const limit = company.pack.tripLimit || 1;
    const progress = `${used}/${limit}`;

    if (company.hasContactedSales) {
      return {
        label: 'Contacté',
        progress,
        severity: 'success',
        icon: 'pi pi-envelope',
      };
    }

    if (used >= limit) {
      return {
        label: 'Limite',
        progress,
        severity: 'secondary',
        icon: 'pi pi-exclamation-triangle',
      };
    }

    if (used > 0) {
      return {
        label: 'En test',
        progress,
        severity: 'info',
        icon: 'pi pi-clock',
      };
    }

    return {
      label: 'Discovery',
      progress,
      severity: 'secondary',
      icon: 'pi pi-star',
    };
  }

  /**
   * Récupère les classes CSS pour les tags selon le design system
   */
  getTagClasses(company: Company): string {
    if (company.pack.code !== 'FREE') {
      return 'bg-success-50 text-success-800 border-success-200';
    }

    const used = company._count?.trips || 0;
    const limit = company.pack.tripLimit || 1;

    if (company.hasContactedSales) {
      return 'bg-success-50 text-success-800 border-success-200';
    }

    if (used >= limit) {
      return 'bg-greyscale-50 text-greyscale-700 border-greyscale-200';
    }

    if (used > 0) {
      return 'bg-primary-50 text-primary-800 border-primary-200';
    }

    return 'bg-greyscale-50 text-greyscale-700 border-greyscale-200';
  }

  editCompany(id: string) {
    // Deleted companies can't be edited (backend excludes them), only reactivated.
    if (this.showDeleted) {
      return;
    }
    this.router.navigate(['/admin/company/edit', id]);
  }

  confirmReactivateCompany(id: string) {
    this.confirmationService.confirm({
      message:
        'Are you sure you want to reactivate this company? It and its users will regain access.',
      header: 'Reactivate Confirmation',
      icon: 'pi pi-refresh',
      accept: () => this.reactivateCompany(id),
    });
  }

  reactivateCompany(id: string) {
    this.companyService.activate(id).subscribe({
      next: () => {
        this.deletedCompanies = this.deletedCompanies.filter(
          (c) => c.id !== id,
        );
        this.notificationService.success('Company reactivated successfully');
      },
      error: () => {
        this.notificationService.error('Error reactivating company');
      },
    });
  }

  confirmDeleteCompany(id: string) {
    this.confirmationService.confirm({
      message:
        'Are you sure you want to delete this company? This will also delete all associated users and trips.',
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.deleteCompany(id),
    });
  }

  deleteCompany(id: string) {
    this.companyService.delete(id).subscribe({
      next: () => {
        this.companies = this.companies.filter((c) => c.id !== id);
        this.notificationService.success('Company deleted successfully');
      },
      error: () => {
        this.notificationService.error('Error deleting company');
      },
    });
  }
}
